import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../lib/api";
import PhotoCard from "../components/PhotoCard";
import EditorialMarquee from "../components/Marquee";

const FILTERS = ["All", "Landscape", "Nature", "Portrait", "Street", "Cityscape", "Structure", "Architecture"];

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== null) setQuery(q);
  }, [searchParams]);

  useEffect(() => {
    API.get("/photos")
      .then((r) => setPhotos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const visible = photos.filter((p) => {
    if (filter !== "All" && p.category !== filter) return false;
    if (q && !`${p.title} ${p.description}`.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div data-testid="gallery-page" className="pt-20">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-16">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-xs tracking-[0.2em] uppercase font-medium text-zinc-500 block mb-6"
        >
          The Full Collection
        </motion.span>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-white" data-testid="gallery-title">
          <span className="mask-line">
            <motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ delay: 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}>
              Every frame,
            </motion.span>
          </span>
          <span className="mask-line">
            <motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ delay: 0.3, duration: 1, ease: [0.22, 1, 0.36, 1] }}>
              <em className="text-[#D4AF37]">for sale.</em>
            </motion.span>
          </span>
        </h1>
        <div className="mt-12 flex items-center gap-3 flex-nowrap overflow-x-auto pb-2 md:flex-wrap md:overflow-visible md:pb-0" data-testid="gallery-filters">
          {q && (
            <div className="flex items-center gap-3 mr-2">
              <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">
                Results for <em className="text-[#D4AF37] not-italic">“{query.trim()}”</em>
              </span>
              <button
                onClick={() => setQuery("")}
                data-testid="clear-search-button"
                className="text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-white transition-colors duration-300"
              >
                Clear
              </button>
            </div>
          )}
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`shrink-0 text-xs tracking-[0.2em] uppercase font-medium px-6 py-3 border transition-colors duration-300 ${
                filter === f
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-white/15 text-zinc-500 hover:border-white/40 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-32">
        {loading ? (
          <div className="h-64 flex items-center justify-center text-zinc-600 text-sm tracking-[0.2em] uppercase" data-testid="gallery-loading">
            Loading the collection
          </div>
        ) : visible.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-zinc-600 text-sm tracking-[0.2em] uppercase" data-testid="gallery-empty">
            {query.trim() ? "No photographs match your search" : "No photographs in this category yet"}
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 xl:columns-3 gap-6 md:gap-8" data-testid="gallery-grid">
            {visible.map((photo, i) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                index={i}
                natural
                className="mb-6 md:mb-8 break-inside-avoid"
              />
            ))}
          </div>
        )}
      </section>

      <EditorialMarquee />
    </div>
  );
}
